import {
  ActivePeriodsFilterConfig,
  AvgSessionLengthFilterConfig,
  AboveValueConfigKind,
  BelowValueConfigKind,
  BetweenValueConfigKind,
  CohortFilterGranularity,
  InTheLastValueConfig,
  InTheLastValueConfigKind,
} from '@/api/generated/model';
import {
  formatActivePeriodsPhrase,
  formatAvgSessionLengthPhrase,
} from '@/domains/live-activity/cohorts/components/rule-builder/cohort-expression-format';
import {
  createDefaultActivePeriodsFilter,
  createDefaultAvgSessionLengthFilter,
} from '@/domains/live-activity/cohorts/components/rule-builder/cohort-user-filters';
import { FILTERS } from '@/models/common/filters';
import {
  ValueComparatorKind,
  VALUE_COMPARATOR_KINDS,
} from '@/comps/FilterPicker/filter-content-v2/interactionSpecialFilterAdapter';

export type ActivePeriodsFilterState = {
  column: typeof FILTERS.activePeriods;
  comparatorKind: ValueComparatorKind;
  firstNumber: number;
  secondNumber: number;
  window: number;
  granularity: CohortFilterGranularity;
};

export type AvgSessionLengthFilterState = {
  column: typeof FILTERS.avgSessionLength;
  comparatorKind: ValueComparatorKind;
  firstNumber: number;
  secondNumber: number;
  windowValue: number;
  windowGranularity: CohortFilterGranularity;
};

export type UserMetricSpecialFilterState = ActivePeriodsFilterState | AvgSessionLengthFilterState;

export type UserMetricSpecialFilterValues =
  | ActivePeriodsFilterConfig['values']
  | AvgSessionLengthFilterConfig['values'];

function buildCountConfig(
  kind: ValueComparatorKind,
  first: number,
  second: number,
): ActivePeriodsFilterConfig['values']['active_count'] | AvgSessionLengthFilterConfig['values']['duration'] {
  const offset = new Date().getTimezoneOffset();

  if (kind === BetweenValueConfigKind.between) {
    return {
      kind: BetweenValueConfigKind.between,
      value: [first, second] as [number, number],
      offset,
    };
  }

  if (kind === BelowValueConfigKind.below) {
    return {
      kind: BelowValueConfigKind.below,
      value: first,
      offset,
    };
  }

  return {
    kind: AboveValueConfigKind.above,
    value: first,
    offset,
  };
}

function parseCountConfig(config: { kind?: string; value?: number | number[] } | undefined): {
  comparatorKind: ValueComparatorKind;
  firstNumber: number;
  secondNumber: number;
} {
  const kind = (config?.kind ?? AboveValueConfigKind.above) as ValueComparatorKind;

  if (kind === BetweenValueConfigKind.between && Array.isArray(config?.value)) {
    return {
      comparatorKind: kind,
      firstNumber: config.value[0] ?? 6,
      secondNumber: config.value[1] ?? 6,
    };
  }

  return {
    comparatorKind:
      kind === BelowValueConfigKind.below
        ? BelowValueConfigKind.below
        : kind === BetweenValueConfigKind.between
          ? BetweenValueConfigKind.between
          : AboveValueConfigKind.above,
    firstNumber: typeof config?.value === 'number' ? config.value : kind === BetweenValueConfigKind.between ? 6 : 120,
    secondNumber: 6,
  };
}

export function isUserMetricSpecialFilter(_column: string | null | undefined): boolean {
  // active_periods and avg_session_length are handled by isInteractionSpecialFilter, which supports
  // the full comparator set (including exactly) and avg-session duration units.
  return false;
}

export function getUserMetricComparatorFromValues(column: string, values: unknown): ValueComparatorKind | null {
  if (!isUserMetricSpecialFilter(column) || !values || typeof values !== 'object') return null;

  if (column === FILTERS.activePeriods) {
    return parseCountConfig((values as ActivePeriodsFilterConfig['values']).active_count).comparatorKind;
  }

  return parseCountConfig((values as AvgSessionLengthFilterConfig['values']).duration).comparatorKind;
}

export function parseUserMetricSpecialFilterValues(column: string, values: unknown): UserMetricSpecialFilterState {
  if (column === FILTERS.activePeriods) {
    const config = (values ?? createDefaultActivePeriodsFilter().values) as ActivePeriodsFilterConfig['values'];
    const parsed = parseCountConfig(config.active_count);
    return {
      column: FILTERS.activePeriods,
      comparatorKind: parsed.comparatorKind,
      firstNumber: parsed.firstNumber,
      secondNumber: parsed.secondNumber,
      window: config.window ?? 12,
      granularity: config.granularity ?? CohortFilterGranularity.week,
    };
  }

  const config = (values ?? createDefaultAvgSessionLengthFilter().values) as AvgSessionLengthFilterConfig['values'];
  const parsed = parseCountConfig(config.duration);
  const defaultDates = createDefaultAvgSessionLengthFilter().values.dates as InTheLastValueConfig;
  const dates: InTheLastValueConfig =
    config.dates &&
    typeof config.dates === 'object' &&
    'kind' in config.dates &&
    config.dates.kind === InTheLastValueConfigKind.in_the_last
      ? (config.dates as InTheLastValueConfig)
      : defaultDates;

  return {
    column: FILTERS.avgSessionLength,
    comparatorKind: parsed.comparatorKind,
    firstNumber: parsed.firstNumber,
    secondNumber: parsed.secondNumber,
    windowValue: dates.value ?? 12,
    windowGranularity: dates.granularity ?? CohortFilterGranularity.week,
  };
}

export function serializeUserMetricSpecialFilterState(
  state: UserMetricSpecialFilterState,
): UserMetricSpecialFilterValues | null {
  if (state.column === FILTERS.activePeriods) {
    return {
      active_count: buildCountConfig(state.comparatorKind, state.firstNumber, state.secondNumber),
      window: state.window,
      granularity: state.granularity,
    };
  }

  return {
    duration: buildCountConfig(state.comparatorKind, state.firstNumber, state.secondNumber),
    dates: {
      kind: InTheLastValueConfigKind.in_the_last,
      value: state.windowValue,
      granularity: state.windowGranularity,
    },
  };
}

export function createDefaultUserMetricSpecialFilterValues(column: string): UserMetricSpecialFilterValues {
  if (column === FILTERS.activePeriods) return createDefaultActivePeriodsFilter().values;
  return createDefaultAvgSessionLengthFilter().values;
}

export function createDefaultUserMetricSpecialState(column: string): UserMetricSpecialFilterState {
  return parseUserMetricSpecialFilterValues(column, createDefaultUserMetricSpecialFilterValues(column));
}

export function createUserMetricSpecialStateForComparator(
  column: string,
  comparatorKind: ValueComparatorKind,
  previous?: UserMetricSpecialFilterState,
): UserMetricSpecialFilterState {
  const base = previous ?? createDefaultUserMetricSpecialState(column);
  return { ...base, comparatorKind };
}

export function formatUserMetricSpecialPillSummary(column: string, values: unknown): string | undefined {
  if (!values || typeof values !== 'object') return undefined;

  if (column === FILTERS.activePeriods) {
    return formatActivePeriodsPhrase(values as ActivePeriodsFilterConfig['values']);
  }

  if (column === FILTERS.avgSessionLength) {
    return formatAvgSessionLengthPhrase(values as AvgSessionLengthFilterConfig['values']);
  }

  return undefined;
}

export { VALUE_COMPARATOR_KINDS };
export type { ValueComparatorKind };
